import { useState } from 'react';
import { RefreshCw, AlertTriangle, Scale, KeyRound, ShieldAlert } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import {
  useComplianceRegister,
  useAttestVendorDpa,
  useDeletionRequests,
  usePlaceLegalHold,
} from '../hooks/useAdminOps';
import {
  EXEMPTION_BASES,
  type DeletionRequestItem,
  type ExemptionBasis,
  type VendorDpaEntry,
} from '../api/adminOps';
import {
  QueueTable,
  StatusChip,
  DetailDrawer,
  ReasonActionBar,
  type ChipTone,
  type QueueColumn,
} from '../components/ui';

const SECTIONS = [
  { id: 'register', label: 'Register' },
  { id: 'deletion', label: 'Deletion requests' },
] as const;
type SectionId = (typeof SECTIONS)[number]['id'];

const DUE_SOON_DAYS = 30;

const EXEMPTION_LABELS: Record<ExemptionBasis, string> = {
  irs_recordkeeping: 'IRS recordkeeping',
  litigation_hold: 'Litigation hold',
  regulatory_investigation: 'Regulatory investigation',
  contract_obligation: 'Contract obligation',
};

// Mirrors the backend's DeletionRequestStatus vocabulary.
const DELETION_STATUSES = [
  'awaiting_export',
  'cooling_off',
  'processing',
  'completed',
  'cancelled',
  'exempt',
] as const;
type DeletionStatusFilter = (typeof DELETION_STATUSES)[number] | 'all';

const DELETION_STATUS_TONES: Record<string, { label: string; tone: ChipTone }> = {
  awaiting_export: { label: 'Awaiting export', tone: 'info' },
  cooling_off: { label: 'Cooling off', tone: 'warn' },
  processing: { label: 'Processing', tone: 'info' },
  completed: { label: 'Completed', tone: 'ok' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
  exempt: { label: 'Exempt (legal hold)', tone: 'crit' },
};

function deletionStatusChip(status: string) {
  const config = DELETION_STATUS_TONES[status] ?? { label: status, tone: 'neutral' as ChipTone };
  return <StatusChip label={config.label} tone={config.tone} />;
}

function reviewDueChip(entry: VendorDpaEntry) {
  if (!entry.next_review_due_at) {
    return <StatusChip label="No review scheduled" tone="warn" />;
  }
  const due = new Date(entry.next_review_due_at);
  const msLeft = due.getTime() - Date.now();
  if (msLeft <= 0) {
    return <StatusChip label={`Overdue (${due.toLocaleDateString()})`} tone="crit" />;
  }
  const daysLeft = msLeft / 86_400_000;
  const tone: ChipTone = daysLeft <= DUE_SOON_DAYS ? 'warn' : 'ok';
  return <StatusChip label={`Due ${due.toLocaleDateString()}`} tone={tone} />;
}

/**
 * The wire item does not expose the deletion request's own id yet, even though
 * the legal-hold route is keyed by it; tolerate an `id` if the API sends one.
 */
type DeletionRow = DeletionRequestItem & { id?: string };

function ErrorCard({
  title,
  error,
  onRetry,
}: {
  title: string;
  error: unknown;
  onRetry: () => void;
}) {
  return (
    <div className="glass-card p-6 border border-red-500/30 flex items-start gap-3">
      <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
      <div>
        <p className="text-white font-medium mb-1">{title}</p>
        <p className="text-sm text-gray-text mb-3">
          {error instanceof Error ? error.message : 'Unknown error'}
        </p>
        <button onClick={onRetry} className="btn-secondary">
          Retry
        </button>
      </div>
    </div>
  );
}

function DetailField({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <p className="text-xs text-gray-text uppercase tracking-wide mb-1">{label}</p>
      <p className="text-sm text-white break-words">{value ?? '—'}</p>
    </div>
  );
}

// ============================================================================
// Register section
// ============================================================================

function RegisterSection() {
  const register = useComplianceRegister();
  const attestMutation = useAttestVendorDpa();
  const [attestSlug, setAttestSlug] = useState<string | null>(null);
  const [nextReviewDate, setNextReviewDate] = useState('');
  const [notes, setNotes] = useState('');

  const data = register.data?.data;
  const attesting = data?.vendor_dpa.find((entry) => entry.vendor_slug === attestSlug) ?? null;

  const openAttest = (entry: VendorDpaEntry) => {
    setAttestSlug(entry.vendor_slug);
    setNextReviewDate('');
    setNotes('');
  };

  const closeAttest = () => setAttestSlug(null);

  const submitAttest = () => {
    if (!attesting || nextReviewDate === '') return;
    attestMutation.mutate(
      {
        slug: attesting.vendor_slug,
        nextReviewDueAt: new Date(`${nextReviewDate}T00:00:00Z`).toISOString(),
        notes: notes.trim() === '' ? undefined : notes.trim(),
      },
      { onSuccess: closeAttest }
    );
  };

  const columns: QueueColumn<VendorDpaEntry>[] = [
    {
      key: 'vendor',
      header: 'Vendor',
      render: (entry) => <span className="text-white font-medium">{entry.vendor_slug}</span>,
    },
    {
      key: 'dpa_status',
      header: 'DPA status',
      render: (entry) => (
        <StatusChip
          label={entry.dpa_status}
          tone={entry.dpa_status === 'signed' ? 'ok' : 'warn'}
        />
      ),
    },
    {
      key: 'last_reviewed',
      header: 'Last attested',
      render: (entry) => (
        <span className="text-gray-text whitespace-nowrap">
          {entry.last_reviewed_at
            ? formatDistanceToNow(new Date(entry.last_reviewed_at), { addSuffix: true })
            : 'never'}
        </span>
      ),
    },
    { key: 'next_review', header: 'Next review', render: (entry) => reviewDueChip(entry) },
    {
      key: 'action',
      header: '',
      render: (entry) => (
        <button
          onClick={(event) => {
            event.stopPropagation();
            openAttest(entry);
          }}
          className="btn-ghost text-primary text-sm"
        >
          Attest review
        </button>
      ),
    },
  ];

  if (register.isError) {
    return (
      <ErrorCard
        title="Failed to load the compliance register"
        error={register.error}
        onRetry={() => register.refetch()}
      />
    );
  }

  return (
    <>
      {/* Count tiles */}
      <div className="grid grid-cols-2 gap-4">
        <div className="glass-card p-4 flex items-center gap-4">
          <KeyRound
            className={`w-8 h-8 ${data && data.kms_stale_key_count > 0 ? 'text-red-400' : 'text-green-400'}`}
          />
          <div>
            <div className="text-2xl font-bold text-white">
              {register.isLoading ? '…' : (data?.kms_stale_key_count ?? '—')}
            </div>
            <div className="text-sm text-gray-text">Stale KMS keys (rotation overdue)</div>
          </div>
        </div>
        <div className="glass-card p-4 flex items-center gap-4">
          <ShieldAlert className="w-8 h-8 text-primary" />
          <div>
            <div className="text-2xl font-bold text-white">
              {register.isLoading ? '…' : (data?.coppa_blocked_attempts ?? '—')}
            </div>
            <div className="text-sm text-gray-text">COPPA blocked signup attempts</div>
          </div>
        </div>
      </div>

      {/* Vendor DPA register */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-3">Vendor DPA register</h2>
        <QueueTable
          columns={columns}
          rows={data?.vendor_dpa ?? []}
          rowKey={(entry) => entry.vendor_slug}
          isLoading={register.isPending}
          onRowClick={openAttest}
          emptyTitle="No vendors registered"
          emptyHint="The vendor DPA register is empty."
        />
      </div>

      {/* Attest drawer */}
      <DetailDrawer
        open={attesting !== null}
        title={`Attest DPA review — ${attesting?.vendor_slug ?? ''}`}
        onClose={closeAttest}
        footer={
          attesting && (
            <button
              onClick={submitAttest}
              disabled={nextReviewDate === '' || attestMutation.isPending}
              className="btn-primary w-full disabled:opacity-50"
            >
              {attestMutation.isPending ? 'Recording…' : 'Record attestation'}
            </button>
          )
        }
      >
        {attesting && (
          <>
            <div className="flex items-center gap-2">{reviewDueChip(attesting)}</div>
            <p className="text-sm text-gray-text">
              Recording an attestation stamps today as the last review and schedules the next
              one. It lands in the audit ledger under your identity.
            </p>
            <div>
              <label className="label" htmlFor="attest-next-review">
                Next review due
              </label>
              <input
                id="attest-next-review"
                type="date"
                className="input"
                value={nextReviewDate}
                onChange={(event) => setNextReviewDate(event.target.value)}
              />
            </div>
            <div>
              <label className="label" htmlFor="attest-notes">
                Notes (optional)
              </label>
              <textarea
                id="attest-notes"
                className="input min-h-[80px]"
                placeholder="What was reviewed, findings, links"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
              />
            </div>
          </>
        )}
      </DetailDrawer>
    </>
  );
}

// ============================================================================
// Deletion requests section
// ============================================================================

function DeletionRequestsSection() {
  const [statusFilter, setStatusFilter] = useState<DeletionStatusFilter>('cooling_off');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const queue = useDeletionRequests(statusFilter === 'all' ? {} : { status: statusFilter });
  const legalHoldMutation = usePlaceLegalHold();

  const rows = queue.rows as DeletionRow[];
  const selected = rows.find((row) => row.user_id === selectedUserId) ?? null;
  const closeDrawer = () => setSelectedUserId(null);

  const columns: QueueColumn<DeletionRow>[] = [
    {
      key: 'requested_at',
      header: 'Requested',
      render: (row) => (
        <span className="text-gray-text whitespace-nowrap">
          {formatDistanceToNow(new Date(row.requested_at), { addSuffix: true })}
        </span>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      render: (row) => <span className="text-white">{row.deletion_type ?? '—'}</span>,
    },
    { key: 'status', header: 'Status', render: (row) => deletionStatusChip(row.status) },
    {
      key: 'cooling_off',
      header: 'Cooling-off until',
      render: (row) => {
        const until = new Date(row.cooling_off_until);
        const elapsed = until.getTime() <= Date.now();
        return (
          <span className={`whitespace-nowrap ${elapsed ? 'text-gray-text' : 'text-white'}`}>
            {elapsed
              ? 'elapsed'
              : formatDistanceToNow(until, { addSuffix: true })}
          </span>
        );
      },
    },
  ];

  return (
    <>
      {/* Status filter chips */}
      <div className="glass-card p-4 flex items-center gap-2 flex-wrap">
        <span className="text-sm text-gray-text mr-2">Status:</span>
        {[...DELETION_STATUSES, 'all' as const].map((value) => (
          <button
            key={value}
            onClick={() => {
              setStatusFilter(value);
              setSelectedUserId(null);
            }}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              statusFilter === value
                ? 'bg-primary/20 text-primary'
                : 'bg-gray-500/10 text-gray-text hover:text-white'
            }`}
          >
            {DELETION_STATUS_TONES[value]?.label ?? 'All'}
          </button>
        ))}
      </div>

      {queue.isError ? (
        <ErrorCard
          title="Failed to load deletion requests"
          error={queue.error}
          onRetry={() => queue.refetch()}
        />
      ) : (
        <QueueTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.user_id}
          isLoading={queue.isPending}
          onRowClick={(row) => setSelectedUserId(row.user_id)}
          emptyTitle="No deletion requests"
          emptyHint={
            statusFilter === 'all'
              ? 'No account deletion requests on file.'
              : `No requests with status "${statusFilter}".`
          }
          hasNextPage={queue.hasNextPage}
          isFetchingNextPage={queue.isFetchingNextPage}
          onLoadMore={() => queue.fetchNextPage()}
        />
      )}

      {/* Detail drawer */}
      <DetailDrawer
        open={selected !== null}
        title="Deletion request"
        onClose={closeDrawer}
        footer={
          selected &&
          selected.status !== 'exempt' &&
          selected.status !== 'completed' &&
          selected.status !== 'cancelled' ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-text">
                Placing a legal hold is <span className="text-white">dual-controlled</span>: it
                returns 202 and lands in the Approvals queue as a pending action.
              </p>
              {selected.id === undefined ? (
                <p className="text-sm text-yellow-400">
                  This queue does not return the deletion request's id yet, so the legal-hold
                  action is unavailable from this page.
                </p>
              ) : (
                <ReasonActionBar
                  reasons={EXEMPTION_BASES.map((basis) => ({
                    value: basis,
                    label: EXEMPTION_LABELS[basis],
                  }))}
                  actionLabel="Place legal hold"
                  destructive
                  isPending={legalHoldMutation.isPending}
                  notePlaceholder="Case reference, custodian, scope"
                  onSubmit={(reason, note) => {
                    if (selected.id === undefined) return;
                    legalHoldMutation.mutate(
                      {
                        deletionRequestId: selected.id,
                        exemptionBasis: reason as ExemptionBasis,
                        notes: note,
                      },
                      { onSuccess: closeDrawer }
                    );
                  }}
                />
              )}
            </div>
          ) : null
        }
      >
        {selected && (
          <>
            <div className="flex items-center gap-2">{deletionStatusChip(selected.status)}</div>
            <DetailField label="User" value={selected.user_id} />
            <DetailField label="Deletion type" value={selected.deletion_type} />
            <DetailField
              label="Requested"
              value={new Date(selected.requested_at).toLocaleString()}
            />
            <DetailField
              label="Cooling-off until"
              value={new Date(selected.cooling_off_until).toLocaleString()}
            />
            <DetailField
              label="Completed"
              value={
                selected.completed_at ? new Date(selected.completed_at).toLocaleString() : null
              }
            />
            {selected.exemption_basis && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 space-y-1">
                <p className="text-sm font-medium text-red-400">Legal hold in place</p>
                <p className="text-sm text-white">
                  {EXEMPTION_LABELS[selected.exemption_basis as ExemptionBasis] ??
                    selected.exemption_basis}
                </p>
                <p className="text-xs text-gray-text">
                  Set by {selected.exemption_set_by ?? 'unknown'}
                  {selected.exemption_set_at &&
                    ` ${formatDistanceToNow(new Date(selected.exemption_set_at), { addSuffix: true })}`}
                </p>
              </div>
            )}
          </>
        )}
      </DetailDrawer>
    </>
  );
}

// ============================================================================
// Page
// ============================================================================

export default function Compliance() {
  const [section, setSection] = useState<SectionId>('register');
  const register = useComplianceRegister();

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <Scale className="w-7 h-7 text-primary" />
            Compliance
          </h1>
          <p className="text-gray-text">
            Vendor DPA register, key-rotation posture, and account-deletion / legal-hold
            workflow.
          </p>
        </div>
        <button
          onClick={() => register.refetch()}
          className="btn-secondary flex items-center space-x-2"
          disabled={register.isFetching}
        >
          <RefreshCw className={`w-4 h-4 ${register.isFetching ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Section tabs */}
      <div className="glass-card p-2 flex items-center gap-1">
        {SECTIONS.map((entry) => (
          <button
            key={entry.id}
            onClick={() => setSection(entry.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              section === entry.id
                ? 'bg-primary/20 text-primary'
                : 'text-gray-text hover:text-white'
            }`}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {section === 'register' && <RegisterSection />}
      {section === 'deletion' && <DeletionRequestsSection />}
    </div>
  );
}
